/*
  Promo landings are also reachable on their own subdomain:

    school.promo.shop.anvar.kz  ->  the landing whose code is "school"

  The site is a single SPA, so the subdomain is not a separate build: the host
  is read at runtime, the landing code is taken from the first label, and the
  router is pointed at the usual /landing/:code route. Everything downstream
  (data loading, header variant, blocks) is unchanged.
*/

/*
  <code>.promo.shop.anvar.kz — the code is the first label only. The pattern is
  anchored so an unrelated host with a similar shape never matches, and the
  base domain is kept in one place rather than spread across call sites.
*/
const PROMO_HOST_PATTERN = /^([^.]+)\.promo\.shop\.anvar\.kz$/i

/** Landing code carried by the current host, or null on the main domain. */
export function getPromoLandingCode (hostname?: string): string | null {
  const host = hostname ?? (typeof window === 'undefined' ? '' : window.location.hostname)

  const match = host.match(PROMO_HOST_PATTERN)

  return match ? match[1].toLowerCase() : null
}

/** True when the app is being served from a promo landing subdomain. */
export function isPromoSubdomain (hostname?: string): boolean {
  return getPromoLandingCode(hostname) !== null
}

/*
  Absolute origin of the main site, for links that must leave the promo
  subdomain — chiefly the logo, whose "/" would otherwise land back on the
  subdomain root and bounce straight to the landing again.

  The origin is derived from the current host (drop the "<code>.promo." prefix)
  rather than read from config, so it follows the environment the visitor is
  actually on. Returns null off a promo host, where in-app links are already
  correct and should stay relative.
*/
export function getMainSiteOrigin (): string | null {
  if (typeof window === 'undefined' || !isPromoSubdomain()) {
    return null
  }

  const { protocol, host } = window.location

  // host keeps any :port, which the dev/staging setups rely on.
  return `${protocol}//${host.replace(/^[^.]+\.promo\./i, '')}`
}
